import { MoreHorizontal } from 'lucide-react';
import { Avatar } from './ParticipantChip';
import type { Classification } from './ParticipantChip';

export interface ThreadMessage {
  id: string;
  authorName: string;
  authorClassification: Classification;
  body: string;
  /**
   * The rendered timestamp, or `null` when the bubble below carries an identical one. Decided by
   * `messageRuns`, never by this component — the answer depends on the neighbour, which a single
   * bubble cannot see.
   */
  time: string | null;
  own?: boolean;
  /** True only for the first bubble in a run by the same author. */
  startsRun?: boolean;
  /**
   * The tombstone — §5, TROOP-12. The thread must always show that a message existed, who wrote it
   * and when; what it surrenders is the words. Never a silent disappearance, which is the WhatsApp
   * behaviour that leaves a troop unable to demonstrate what happened.
   */
  removed?: boolean;
  /** `AuthorDeleted` or `ModeratorRemoved`. Which one is part of the record, so it is rendered. */
  removalBasis?: string | null;
}

/**
 * Max width 74%. Others' messages white with a 1px subtle border and a 4px
 * top-left tail; own messages inverse-filled, right-aligned, tail top-right.
 *
 * Only the first bubble in a run gets the tail and the name row (spec §6), and only the last bubble
 * of a same-minute run gets a timestamp. Both are decided by `messageRuns` and handed in, because
 * both are questions about a bubble's neighbours and a single bubble cannot see them.
 *
 * A youth's message carries **only** the square avatar — no badge, no label on
 * the name. Status is confirmable in the participant strip; it is not stamped on
 * a child in conversation (spec §6, §10).
 *
 * **A removed message keeps its frame and loses its fill** (TROOP-12). It is deliberately not
 * dimmed away to nothing: the tombstone is the product feature, so it takes the same space in the
 * stream as the message it replaces, keeps the author's name and avatar, keeps the timestamp, and
 * says in words which kind of removal it was. It is drawn the same way whoever is reading —
 * including its own author, who reads the words back through a separate deliberate ask rather than
 * having them quietly still be here (§5).
 *
 * **SPEC-GAP:** `handoff/design-system.md` §6 describes the bubble and says nothing about a
 * tombstone or a per-message control, so both are built from existing tokens only — the dashed
 * border is `--color-border-subtle`, the text `--color-text-muted`, and no new colour is
 * introduced. Status is never carried in colour alone here either: the tombstone says what it is.
 */
export function MessageBubble({
  message,
  onOpenActions,
}: {
  message: ThreadMessage;
  /**
   * Opens the sheet of things this reader can do with this message — report it, take it down, put
   * it back. Optional: a screen that has not wired moderation renders no control at all rather
   * than a dead one, which is the same reasoning `Button` applies to `disabledReason`.
   */
  onOpenActions?: () => void;
}) {
  const { own, startsRun = true, removed = false } = message;

  const initials = message.authorName
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('');

  return (
    <div
      className="flex flex-col"
      style={{ marginTop: startsRun ? 'var(--space-3)' : 'var(--space-1)' }}
    >
      {/* The name sits above the whole row, offset past the avatar column, so the
          avatar can align to the top of the bubble rather than being dragged to
          the bottom by the timestamp beneath it. */}
      {startsRun && !own && (
        <p
          className="t-caption"
          style={{
            color: 'var(--color-text-secondary)',
            margin: '0 0 var(--space-1)',
            paddingInlineStart: 'calc(26px + var(--space-2) + var(--space-1))',
          }}
        >
          {message.authorName}
        </p>
      )}

      <div
        className="flex gap-2"
        style={{
          justifyContent: own ? 'flex-end' : 'flex-start',
          alignItems: 'flex-start',
        }}
      >
        {!own && (
          <span style={{ width: 26, flexShrink: 0 }}>
            {startsRun && (
              <Avatar initials={initials} classification={message.authorClassification} />
            )}
          </span>
        )}

        <div style={{ maxWidth: '74%', minWidth: 0 }}>
          <div
          style={{
            // A tombstone is never inverse-filled, even when it is your own. The fill is what
            // says "these are somebody's words", and the words are exactly what has gone.
            background: removed
              ? 'transparent'
              : own
                ? 'var(--color-surface-inverse)'
                : 'var(--color-surface-raised)',
            color: removed
              ? 'var(--color-text-muted)'
              : own
                ? 'var(--color-text-on-inverse)'
                : 'var(--color-text-primary)',
            border: removed
              ? '1px dashed var(--color-border-subtle)'
              : own
                ? 'none'
                : '1px solid var(--color-border-subtle)',
            borderRadius: removed
              ? '12px'
              : own
                ? startsRun
                  ? '12px 4px 12px 12px'
                  : '12px'
                : startsRun
                  ? '4px 12px 12px 12px'
                  : '12px',
            padding: 'var(--space-3) var(--space-4)',
          }}
        >
            {removed ? (
              <p className="t-body" style={{ margin: 0, fontStyle: 'italic' }}>
                {tombstone(message.removalBasis)}
              </p>
            ) : (
              /* Body is always primary text — never grey-on-grey (spec §6). */
              <p className="t-body" style={{ margin: 0, overflowWrap: 'anywhere' }}>
                {message.body}
              </p>
            )}
          </div>

          {/* Omitted entirely rather than rendered empty. An empty element would still occupy its
              line and its margin, which is exactly the gap that makes a tight run look loose —
              the repetition would be gone and the spacing would still say two separate moments. */}
          {message.time !== null && (
            <p
              className="t-caption tabular"
              style={{
                color: 'var(--color-text-muted)',
                margin: 'var(--space-1) 0 0',
                textAlign: own ? 'right' : 'left',
                paddingInline: 'var(--space-1)',
              }}
            >
              {message.time}
            </p>
          )}
        </div>

        {onOpenActions && (
          <button
            type="button"
            onClick={onOpenActions}
            // Names the author, because a screen reader moving through a thread hears this
            // control once per message and "More" forty times says nothing about which one.
            aria-label={`What you can do with ${message.authorName}'s message`}
            className="flex items-center justify-center rounded-md"
            style={{
              width: 44,
              height: 44,
              flexShrink: 0,
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-muted)',
              cursor: 'pointer',
              // Ahead of the bubble on your own messages, so the control sits on the outside of
              // the stream in both directions rather than wandering into the middle of it.
              order: own ? -1 : 0,
            }}
          >
            <MoreHorizontal size={18} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * What the thread says in place of the words. Which kind of removal it was is part of the record —
 * a leader taking something down and somebody deleting their own message are different facts, and
 * a single "message removed" would flatten them into the one a committee cannot use.
 */
function tombstone(basis: string | null | undefined): string {
  switch (basis) {
    case 'AuthorDeleted':
      return 'This message was deleted by the person who wrote it.';
    case 'ModeratorRemoved':
      return 'This message was taken down by a leader.';
    default:
      return 'This message was removed.';
  }
}
